import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "motion/react";
import type { Agent } from "@baton/contracts";
import { ApiError, type DashboardApi } from "./api.js";
import { usePrefersReducedMotion } from "./flow/reduced-motion.js";
import { inviteUrl } from "./invite.js";
import type { DecryptedProjectGraph } from "./projects-view.js";
import {
  filterPeople,
  inviteMessage,
  mergePeople,
  personHint,
  repositorySummary,
  PERSON_STATE_LABELS,
  type EligibleCollaborator,
  type PersonRow,
} from "./people.js";

/** How long a minted one-click link stays valid; it is single use on top of that. */
const LINK_HOURS = 24 * 7;

/** What happened to one person after the single click. `message` is set only when a link was minted. */
export type PersonOutcome =
  | { kind: "invited"; role: "admin" | "member"; message: string | null }
  | { kind: "error"; message: string };

export interface PeopleListProps {
  rows: PersonRow[];
  /** "compact" is the narrow rail; "full" is the invite dialog. */
  variant?: "full" | "compact";
  /** The viewer is a project admin, so a row's single click sends a real invitation. */
  canInvite: boolean;
  /** The role a new invitation carries; the select is rendered in the full variant only. */
  role: "member" | "admin";
  onRoleChange?: ((role: "member" | "admin") => void) | undefined;
  /** Only an owner may hand out admin. */
  allowAdminRole?: boolean | undefined;
  busyUserId: string | null;
  outcomes: Record<string, PersonOutcome>;
  onInvite(row: PersonRow): void;
  emptyLabel?: string | undefined;
}

/**
 * One row per person, deduplicated across every repository in the project. Presentational: the
 * rows, the outcomes and the invite action all come from the owner (see `useProjectPeople`).
 * Rows are reachable with the arrow keys and Enter invites the focused one.
 */
export function PeopleList(props: PeopleListProps) {
  const compact = props.variant === "compact";
  const staticOnly = usePrefersReducedMotion();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const rowRefs = useRef<Array<HTMLLIElement | null>>([]);

  const visible = useMemo(() => filterPeople(props.rows, query), [props.rows, query]);

  // A shrinking list must not leave the roving tabindex pointing past the end.
  useEffect(() => { setActive((current) => Math.max(0, Math.min(current, visible.length - 1))); }, [visible.length]);

  const invitable = useCallback(
    (row: PersonRow) => props.canInvite && row.state === "not-invited" && props.busyUserId === null,
    [props.canInvite, props.busyUserId],
  );

  function focusRow(index: number) {
    const next = Math.max(0, Math.min(index, visible.length - 1));
    setActive(next);
    rowRefs.current[next]?.focus();
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLUListElement>) {
    if (event.key === "ArrowDown") { event.preventDefault(); focusRow(active + 1); return; }
    if (event.key === "ArrowUp") { event.preventDefault(); focusRow(active - 1); return; }
    if (event.key !== "Enter") return;
    const row = visible[active];
    if (row && invitable(row)) { event.preventDefault(); props.onInvite(row); }
  }

  // `false` is motion's own "start where you are": the reduced-motion path renders no entrance.
  const enter = staticOnly ? false : { opacity: 0, y: 4 };
  const badgeEnter = staticOnly ? false : { opacity: 0, scale: 0.88 };
  const timing = { duration: staticOnly ? 0 : 0.18, ease: "easeOut" as const };

  return <div className={`people-list${compact ? " compact" : ""}`} data-testid="people-list">
    <div className="people-list-controls">
      <input
        type="search"
        className="people-search"
        data-testid="people-search"
        value={query}
        placeholder={compact ? "Search people" : "Search by handle or repository"}
        aria-label="Search people"
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => { if (event.key === "ArrowDown") { event.preventDefault(); focusRow(0); } }}
      />
      {!compact && props.onRoleChange && <label className="invite-people-role">Invite as
        <select value={props.role} onChange={(event) => props.onRoleChange?.(event.target.value as "member" | "admin")}>
          <option value="member">member</option>
          {props.allowAdminRole && <option value="admin">admin</option>}
        </select>
      </label>}
    </div>

    <p className="visually-hidden" role="status" aria-live="polite">
      {visible.length === props.rows.length
        ? `${visible.length} ${visible.length === 1 ? "person" : "people"}`
        : `${visible.length} of ${props.rows.length} people match`}
    </p>

    {visible.length === 0 && <p className="empty compact" data-testid="people-empty">
      {props.rows.length === 0 ? props.emptyLabel ?? "Nobody else can reach this project's repositories yet." : "No match."}
    </p>}

    {visible.length > 0 && <ul className="people-rows" onKeyDown={onKeyDown} aria-label="People on this project">
      {visible.map((row, index) => {
        const outcome = props.outcomes[row.userId];
        const repositories = repositorySummary(row.repositories);
        const busy = props.busyUserId === row.userId;
        return <motion.li
          key={row.userId}
          ref={(element: HTMLLIElement | null) => { rowRefs.current[index] = element; }}
          data-testid="person-row"
          data-state={row.state}
          className="person-row"
          tabIndex={index === active ? 0 : -1}
          onFocus={() => setActive(index)}
          initial={enter}
          animate={{ opacity: 1, y: 0 }}
          transition={timing}
        >
          <span className="person-main">
            <span className="person-head">
              <strong className="person-handle" title={row.githubLogin ? `GitHub @${row.githubLogin}` : `@${row.handle}`}>@{row.handle}</strong>
              <motion.span
                key={row.state}
                className={`person-badge state-${row.state}`}
                data-testid="person-badge"
                initial={badgeEnter}
                animate={{ opacity: 1, scale: 1 }}
                transition={timing}
              >{PERSON_STATE_LABELS[row.state]}</motion.span>
            </span>
            <small className="muted person-hint">{personHint(row)}</small>
            {repositories.chips.length > 0 && <span className="person-repos" title={repositories.title}>
              {repositories.chips.map((repository) => <span className="person-repo" key={repository.id}>{repository.label}</span>)}
              {repositories.more > 0 && <span className="person-repo more">+{repositories.more} more</span>}
            </span>}
            {outcome?.kind === "error" && <small className="error" role="status">{outcome.message}</small>}
            {outcome?.kind === "invited" && outcome.message === null
              && <small className="person-sent" role="status">In-app invitation created as {outcome.role}. Email is a separate action in the Invite dialog.</small>}
          </span>
          {invitable(row) || busy
            ? <button
              type="button"
              className="person-invite"
              disabled={props.busyUserId !== null}
              onClick={() => props.onInvite(row)}
            >{busy ? "Inviting…" : "Invite"}</button>
            : null}
          {outcome?.kind === "invited" && outcome.message !== null
            && <CopiedNote message={outcome.message} role={outcome.role} />}
        </motion.li>;
      })}
    </ul>}
  </div>;
}

/**
 * The note copied for someone who has never signed in, shown so the inviter can see exactly what
 * went to the clipboard. Text content only: never an attribute, never storage, never a log.
 */
function CopiedNote({ message, role }: { message: string; role: "admin" | "member" }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1_800);
    return () => clearTimeout(timer);
  }, [copied]);
  return <aside className="person-copied" data-testid="invite-copied" role="status">
    <strong>Invited as {role}. This message is on your clipboard — paste it to them.</strong>
    <pre className="person-copied-text">{message}</pre>
    <button type="button" className="link" onClick={() => {
      void navigator.clipboard.writeText(message).then(() => setCopied(true), () => undefined);
    }}>{copied ? "Copied" : "Copy again"}</button>
  </aside>;
}

export interface ProjectPeopleState {
  rows: PersonRow[];
  error: string | null;
  loading: boolean;
  busyUserId: string | null;
  outcomes: Record<string, PersonOutcome>;
  /** The viewer is a project admin: the eligible listing is readable and invitations are allowed. */
  canInvite: boolean;
  invite(row: PersonRow, role: "member" | "admin"): Promise<void>;
  reload(): Promise<void>;
}

/**
 * Loads the project's people once for the whole cockpit and runs the one-click invitation. The
 * eligible-collaborator listing is admin-only on the server, so a plain member gets the graph's
 * members alone rather than a 403.
 */
export function useProjectPeople(input: {
  api: DashboardApi;
  graph: DecryptedProjectGraph | null;
  agents: Agent[];
  canInvite: boolean;
  /** Dashboard origin used to build a join URL when the API returns a token only. */
  origin: string;
}): ProjectPeopleState {
  const { api, graph, agents, canInvite, origin } = input;
  const [collaborators, setCollaborators] = useState<EligibleCollaborator[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);
  const [outcomes, setOutcomes] = useState<Record<string, PersonOutcome>>({});
  const projectId = graph?.project.id ?? null;
  const projectName = graph?.project.name ?? "";

  const reload = useCallback(async () => {
    // Any active member may read the collaborator list (the server dropped this to
    // #requireActiveProjectMember); only the invite action is admin-only.
    if (!projectId) { setCollaborators([]); setError(null); return; }
    setLoading(true);
    try {
      setCollaborators(await api.eligibleProjectCollaborators(projectId));
      setError(null);
    } catch (caught) {
      setCollaborators([]);
      setError(caught instanceof Error ? caught.message : "Could not load the people Baton knows");
    } finally {
      setLoading(false);
    }
  }, [api, projectId]);

  useEffect(() => { setOutcomes({}); void reload(); }, [reload]);

  // Signing in provisions a general agent (role null), so owning one proves a Baton account.
  const signedInUserIds = useMemo(
    () => agents.filter((agent) => agent.role === null).map((agent) => agent.ownerUserId),
    [agents],
  );

  const rows = useMemo(() => mergePeople({
    collaborators,
    members: graph?.members ?? [],
    repositories: graph?.repositories ?? [],
    signedInUserIds,
  }), [collaborators, graph, signedInUserIds]);

  const invite = useCallback(async (row: PersonRow, role: "member" | "admin") => {
    if (!projectId) return;
    setBusyUserId(row.userId);
    setOutcomes((current) => { const next = { ...current }; delete next[row.userId]; return next; });
    try {
      const invitation = await api.inviteProjectMember(projectId, row.userId, role);
      let message: string | null = null;
      // Someone who has never signed in has no canvas for the invitation to wait on, so they also
      // get a single-use join link and a note to paste. The note points at the dashboard's own
      // Telegram button — never at the inviter's deep link, which is single use and personal.
      if (row.signedIn === false) {
        const link = await api.createInviteLink(projectId, {
          role,
          expiresAt: new Date(Date.now() + LINK_HOURS * 60 * 60 * 1_000).toISOString(),
          maxUses: 1,
        });
        message = inviteMessage({
          handle: row.handle,
          projectName,
          joinUrl: link.url ?? inviteUrl(origin, link.token),
        });
        await navigator.clipboard.writeText(message).catch(() => undefined);
      }
      setOutcomes((current) => ({ ...current, [row.userId]: { kind: "invited", role: invitation.role, message } }));
      // Re-read so the badge shows the membership the server recorded, not our guess.
      await reload();
    } catch (caught) {
      setOutcomes((current) => ({ ...current, [row.userId]: { kind: "error", message: inviteFailure(caught) } }));
    } finally {
      setBusyUserId(null);
    }
  }, [api, projectId, projectName, origin, reload]);

  return { rows, error, loading, busyUserId, outcomes, canInvite, invite, reload };
}

/** The two failures worth explaining in place; everything else keeps the API's own message. */
function inviteFailure(caught: unknown): string {
  if (caught instanceof ApiError) {
    if (caught.status === 403) return "Only a project admin can invite";
    if (caught.status === 409) return "Already invited";
  }
  return caught instanceof Error ? caught.message : "Could not send the invitation";
}
